import { Schedule } from '@mui/icons-material'
import DateRangeIcon from '@mui/icons-material/DateRange'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import WavingHandIcon from '@mui/icons-material/WavingHand'
import { Chip, useTheme } from '@mui/material'

import { getInvoiceSubStatus, isInvoiceCanceled } from '@shared/invoices'
import { Types } from 'service-api'

export const InvoiceStatus = ({
  invoice,
  order,
}: {
  invoice: Types.InvoiceAdminDto
  order?: Types.CustomOrderAdminDto
}) => {
  const theme = useTheme()
  const isCanceled = isInvoiceCanceled(invoice)
  const subStatus = getInvoiceSubStatus(invoice)
  const cssCommon = {
    fontWeight: 600,
    padding: '4px 8px',
    borderRadius: '8px',
  }

  if (subStatus.isDeclined) {
    return (
      <Chip
        label="DECLINED"
        sx={{
          ...cssCommon,
          backgroundColor: theme.palette.error.shade12,
          color: theme.palette.error.main,
          '& svg': { fill: theme.palette.error.main },
        }}
      />
    )
  }

  if (isCanceled) {
    return (
      <Chip
        label="CANCELED"
        sx={{
          ...cssCommon,
          backgroundColor: theme.palette.error.shade12,
          color: theme.palette.error.main,
          '& svg': { fill: theme.palette.error.main },
        }}
      />
    )
  }

  if (subStatus.isPending && order?.payments?.some((p) => p.step === 'CONFIRMED')) {
    return (
      <Chip
        label="PROCESSING"
        icon={<Schedule />}
        sx={{
          ...cssCommon,
          backgroundColor: theme.palette.info.shade10,
          color: theme.palette.info.main,
          '& svg': { fill: theme.palette.info.main },
        }}
      />
    )
  }

  if (subStatus.isPending) {
    return (
      <Chip
        label="UNCONFIRMED"
        icon={<WarningAmberIcon />}
        sx={{
          ...cssCommon,
          backgroundColor: theme.palette.warning.shade12,
          color: theme.palette.warning.main,
          '& svg': { fill: theme.palette.warning.main },
        }}
      />
    )
  }

  if (subStatus.isBooked) {
    return (
      <Chip
        label="BOOKED"
        icon={<DateRangeIcon />}
        sx={{
          ...cssCommon,
          backgroundColor: theme.palette.primary.shade12,
          color: theme.palette.primary.main,
          '& svg': { fill: theme.palette.primary.main },
        }}
      />
    )
  }

  if (subStatus.isInProgress) {
    return (
      <Chip
        label="CHECKED IN"
        icon={<MeetingRoomIcon />}
        sx={{
          ...cssCommon,
          backgroundColor: theme.palette.success.shade12,
          color: theme.palette.success.main,
          '& svg': { fill: theme.palette.success.main },
        }}
      />
    )
  }

  if (subStatus.isScheduled) {
    return (
      <Chip
        label="SCHEDULED"
        icon={<DateRangeIcon />}
        sx={{
          ...cssCommon,
          backgroundColor: theme.palette.primary.shade12,
          color: theme.palette.primary.main,
          '& svg': { fill: theme.palette.primary.main },
        }}
      />
    )
  }

  if (subStatus.isSold) {
    return (
      <Chip
        label="SOLD"
        sx={{
          ...cssCommon,
          backgroundColor: theme.palette.success.shade12,
          color: theme.palette.success.main,
          '& svg': { fill: theme.palette.success.main },
        }}
      />
    )
  }

  if (subStatus.isCheckedOut) {
    return (
      <Chip
        label="CHECKED OUT"
        icon={<WavingHandIcon />}
        sx={{
          ...cssCommon,
        }}
      />
    )
  }

  if (subStatus.isRequested) {
    return (
      <Chip
        label="REQUESTED"
        icon={<WarningAmberIcon />}
        sx={{
          ...cssCommon,
          backgroundColor: theme.palette.warning.shade12,
          color: theme.palette.warning.main,
          '& svg': { fill: theme.palette.warning.main },
        }}
      />
    )
  }

  return null
}
